// frontend/src/context/AdminContext.js
//
// Completely separate from AuthContext — manages admin session only.
// The admin JWT lives in an HTTP-only cookie ('admin_access_token') set by
// the backend. The frontend calls GET /api/admin/settings to probe liveness;
// a 401 means the cookie is gone/expired.

import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from "react";
import axios from "axios";

export const AdminContext = createContext();

const API_BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:5000";

export const AdminProvider = ({ children }) => {
  const [adminUser, setAdminUser] = useState(null); // { email, name, role, sub }
  const [adminLoading, setAdminLoading] = useState(true);
  // Guard against concurrent verify calls (same pattern as AuthContext)
  const isVerifying = useRef(false);

  /**
   * Probe the backend to confirm the admin_access_token cookie is valid.
   * On success: populate adminUser from the JWT payload embedded in the response.
   * On 401/403: clear adminUser so AdminProtectedRoute redirects to /admin/login.
   */
  const verifyAdmin = useCallback(async () => {
    if (isVerifying.current) return;
    isVerifying.current = true;

    try {
      // GET /api/admin/me verifies the cookie AND returns identity in one call.
      const res = await axios.get(`${API_BASE_URL}/api/admin/me`, {
        withCredentials: true,
      });
      setAdminUser(res.data.admin); // { id, email, name, role }
    } catch (err) {
      // 401 = expired/missing cookie; 403 = not admin
      setAdminUser(null);
    } finally {
      setAdminLoading(false);
      isVerifying.current = false;
    }
  }, []);

  /**
   * Called by AdminAuthCallback after the backend redirect sets the cookie.
   * Stores identity in localStorage for display purposes (name, email).
   * The actual security token remains HTTP-only in the cookie.
   */
  const setAdminFromCallback = useCallback((identity) => {
    // identity = { email, name, role } decoded from URL params or returned by backend
    localStorage.setItem("admin_user", JSON.stringify(identity));
    setAdminUser(identity);
    setAdminLoading(false);
  }, []);

  /**
   * Logout: call backend to clear the cookie, then wipe local state.
   */
  const adminLogout = useCallback(async () => {
    try {
      await axios.post(
        `${API_BASE_URL}/auth/admin/logout`,
        {},
        { withCredentials: true }
      );
    } catch (_) {
      // Ignore errors — proceed with local cleanup regardless
    }
    setAdminUser(null);
    localStorage.removeItem("admin_user");
    window.location.href = "/admin/login";
  }, []);

  // On mount, verify that the admin cookie is still alive
  useEffect(() => {
    verifyAdmin();
  }, [verifyAdmin]);

  return (
    <AdminContext.Provider
      value={{
        adminUser,
        adminLoading,
        verifyAdmin,
        setAdminFromCallback,
        adminLogout,
      }}
    >
      {children}
    </AdminContext.Provider>
  );
};

export const useAdmin = () => useContext(AdminContext);
